import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { signOutAndRedirect } from '../api/axios';
import './systemadmin/systemAdmin.scss';
import { clearSelectedOrgId } from '../services/selectedOrg';
import OfflineRibbon from '../components/connectivity/OfflineRibbon';

/**
 * Chrome for the cross-org System Administrator area. Deliberately separate
 * from the org-scoped Layout: no organisation, no PIN-active user, no
 * child/staff sidebars — just a header and a nav for platform-level pages.
 * Initially the nav has a single item (Waitlist Registrations); it's built to
 * grow as more platform dashboards are added.
 *
 * `sys-scope` on the root carries the area's identity — the lime seam under the
 * banner, the lime rail marker, the lime sub-tab underline. Lime is the logo's
 * diamond colour and appears nowhere in the tenant app, so an operator can tell
 * platform administration from a customer's data at a glance. It is confined to
 * chrome: nothing inside a table is ever lime (see systemAdmin.scss).
 */
const SystemAdminLayout: React.FC = () => {
  const navigate = useNavigate();

  const onSignOut = async () => {
    try {
      clearSelectedOrgId({ forgetLastUsed: true });
      await signOutAndRedirect();
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  return (
    <div className="layout-container sys-scope">
      <div className="layout-fixed-header d-flex justify-content-between align-items-center px-3">
        <div className="d-flex align-items-center gap-3">
          <span className="text-white h5 mb-0">Rustle It<sup className="reg">®</sup></span>
          <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 300 }}>|</span>
          <span className="sys-chip">System Admin</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            onClick={() => navigate('/select-org')}
            className="btn btn-sm d-flex align-items-center gap-2"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 20, color: '#fff', padding: '0.25rem 0.75rem' }}
            title="Back to workspaces"
          >
            <i className="fas fa-th-large" style={{ fontSize: '0.75rem' }}></i>
            <span style={{ fontSize: '0.85rem' }}>Workspaces</span>
          </button>
          <button
            onClick={onSignOut}
            className="btn btn-sm d-flex align-items-center gap-2"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 20, color: '#fff', padding: '0.25rem 0.75rem' }}
            title="Sign out"
          >
            <i className="fas fa-sign-out-alt" style={{ fontSize: '0.75rem' }}></i>
            <span style={{ fontSize: '0.85rem' }}>Sign out</span>
          </button>
        </div>
      </div>

      <OfflineRibbon placement="inline" />

      <div className="layout-main" style={{ display: 'flex' }}>
        <div className="filters-panel open" style={{ width: 280, minWidth: 280 }}>
          <div className="sidebar d-flex flex-column" style={{ minWidth: 260 }}>
            <nav className="sidebar-nav mb-2">
              <NavLink to="/system-admin/overview" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                <i className="fas fa-chart-line" />
                <span>Overview</span>
              </NavLink>
              <NavLink to="/system-admin/waitlist" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                <i className="fas fa-clipboard-list" />
                <span>Waitlist Registrations</span>
              </NavLink>
              <NavLink to="/system-admin/organisations" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                <i className="fas fa-building" />
                <span>Organisations</span>
              </NavLink>
              <NavLink to="/system-admin/pricing" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                <i className="fas fa-tags" />
                <span>Pricing</span>
              </NavLink>
              <NavLink to="/system-admin/errors" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                <i className="fas fa-bug" />
                <span>Errors</span>
              </NavLink>
              <NavLink to="/system-admin/support-staff" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}>
                <i className="fas fa-life-ring" />
                <span>Support staff</span>
              </NavLink>
            </nav>
          </div>
        </div>

        <div className="top-tabs-page" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default SystemAdminLayout;
